/**
 * Utility functions for calculating text metrics
 * Shared across document converters and scrapers
 */

/**
 * Count words in markdown/plain text
 * Removes markdown syntax and counts actual words
 */
export function countWords(text: string): number {
    const plainText = text
        .replace(/[#*_~`[\]()]/g, '') // Remove markdown characters
        .replace(/\n+/g, ' ') // Replace newlines with spaces
        .trim()

    if (!plainText) {
        return 0
    }

    return plainText.split(/\s+/).filter((word) => word.length > 0).length
}

/**
 * Count characters (excluding markdown syntax and whitespace)
 */
export function countCharacters(text: string): number {
    return text.replace(/[#*_~`[\]()]/g, '').replace(/\s/g, '').length
}

/**
 * Count paragraphs (separated by double newline)
 */
export function countParagraphs(text: string): number {
    return text.split(/\n\n+/).filter((p) => p.trim().length > 0).length
}

/**
 * Count lines (non-empty lines)
 */
export function countLines(text: string): number {
    return text.split('\n').filter((line) => line.trim().length > 0).length
}

/**
 * Estimate reading time in minutes
 * @param wordCount - Number of words
 * @param wordsPerMinute - Average reading speed (default: 200)
 * @returns Estimated reading time in minutes (minimum 1)
 */
export function estimateReadingTime(
    wordCount: number,
    wordsPerMinute = 200,
): number {
    const minutes = Math.ceil(wordCount / wordsPerMinute)
    return Math.max(1, minutes) // Minimum 1 minute
}

/**
 * A duration as a player shows it: `m:ss`, or `h:mm:ss` from an hour up.
 * @param seconds - The duration, rounded to the second
 */
export function formatClock(seconds: number): string {
    const total = Math.max(0, Math.round(seconds))
    const hours = Math.floor(total / 3600)
    const minutes = String(Math.floor((total % 3600) / 60)).padStart(
        hours ? 2 : 1,
        '0',
    )
    const rest = String(total % 60).padStart(2, '0')
    return hours ? `${hours}:${minutes}:${rest}` : `${minutes}:${rest}`
}
